import { get } from 'lodash';

/**
 * Values are the keys used in .env* files
 */
export enum ConfigKey {
  APP_VERSION = 'APP_VERSION',
  HTTP_API_URL = 'HTTP_API_URL',
  WS_API_URL = 'WS_API_URL',
  GQL_API_URL = 'GQL_API_URL',
  ORGTREE_API_URL = 'ORGTREE_API_URL',
  ANOMALIES_API_URL = 'ANOMALIES_API_URL',
  USE_LMA_SERVICES = 'USE_LMA_SERVICES',
  MAPBOX_STYLE_DEFAULT = 'MAPBOX_STYLE_DEFAULT',
  MAPBOX_STYLE_SATELLITE = 'MAPBOX_STYLE_SATELLITE',
  MAPBOX_STYLE_DARK = 'MAPBOX_STYLE_DARK',
  MAPBOX_STYLE_STOPP = 'MAPBOX_STYLE_STOPP',
  MAPBOX_STYLE_STOPPSAT = 'MAPBOX_STYLE_STOPPSAT',
  MAPBOX_TOKEN = 'MAPBOX_TOKEN',
  MAPBOX_DEV_TOKEN = 'MAPBOX_DEV_TOKEN',
  KEYCLOAK_REALM = 'KEYCLOAK_REALM',
  KEYCLOAK_AUTH_SERVER_URL = 'KEYCLOAK_AUTH_SERVER_URL',
  KEYCLOAK_SSL_REQUIRED = 'KEYCLOAK_SSL_REQUIRED',
  KEYCLOAK_CLIENT = 'KEYCLOAK_CLIENT',
  KEYCLOAK_PUBLIC_CLIENT = 'KEYCLOAK_PUBLIC_CLIENT',
  KEYCLOAK_CONFIDENTIAL_PORT = 'KEYCLOAK_CONFIDENTIAL_PORT',
  SENTRY_DSN = 'SENTRY_DSN',
  // Feature flag for the daily-briefing panel (LMA_MORNING_BRIEFING_SLM_FINAL_DESIGN).
  // 'true' only in staging while the concept is validated; off elsewhere.
  BRIEFING_ENABLED = 'BRIEFING_ENABLED',
}

/**
 * Look up '_env_' object in global window on non-local environment,
 * otherwise check node process env values.
 */
export function getEnvVariable(key: ConfigKey) {
  // App version is always set by bundler
  if (isInDevelopmentEnvironment() || key === ConfigKey.APP_VERSION) {
    return process.env[key];
  }

  return get(window, `_env_[${key}]`);
}

export function isInDevelopmentEnvironment() {
  return ['development', 'test'].includes(process.env.NODE_ENV);
}

export function isLmaServicesEnabled(): boolean {
  return getEnvVariable(ConfigKey.USE_LMA_SERVICES) === 'true';
}

/**
 * Org-based visibility policy for the AI briefing features. The BRIEFING_ENABLED value is:
 *   - 'false' / 'off' / '0' / 'none'  -> hidden for everyone (prod default)
 *   - 'all' / 'true' / '*' / '' (empty) -> visible to everyone
 *   - a comma-separated list of org keys (e.g. 'oz:123,od:456') -> visible only to users whose
 *     org(s) are in the list
 *
 * NOTE: this controls UI VISIBILITY, not data security (the read API isn't Keycloak-scoped yet).
 *
 * @param userOrgKeys the logged-in user's org key(s) (e.g. from authService.rootOrgKeys)
 */
export function isBriefingVisibleForOrgKeys(userOrgKeys: string[] = []): boolean {
  const raw = (getEnvVariable(ConfigKey.BRIEFING_ENABLED) || '').trim().toLowerCase();
  if (raw === 'false' || raw === 'off' || raw === '0' || raw === 'none') {
    return false;
  }
  if (raw === '' || raw === 'all' || raw === 'true' || raw === '*') {
    return true;
  }
  const allow = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return (userOrgKeys || []).some((k) => k && allow.includes(k.toLowerCase()));
}

/** Backwards-compatible: on when the policy is 'all'/'true'/empty (no per-org restriction). */
export function isBriefingEnabled(): boolean {
  return isBriefingVisibleForOrgKeys([]);
}

/**
 * Returns the base URL for org tree / org unit endpoints.
 * When USE_LMA_SERVICES is true, uses ORGTREE_API_URL (lma-services).
 * When false (default), falls back to HTTP_API_URL (lma-api).
 */
export function getOrgTreeBaseUrl(): string {
  if (isLmaServicesEnabled()) {
    return getEnvVariable(ConfigKey.ORGTREE_API_URL);
  }
  return getEnvVariable(ConfigKey.HTTP_API_URL);
}

/**
 * Returns the base URL for anomalies endpoints (/anomalies/find, /kpis).
 * When USE_LMA_SERVICES is true, uses ANOMALIES_API_URL (lma-services) —
 * falling back to ORGTREE_API_URL if the dedicated var isn't set, since in
 * the common deployment lma-services hosts both modules on the same port.
 * When false, uses HTTP_API_URL (lma-api → lma-anomalies-backend).
 */
export function getAnomaliesBaseUrl(): string {
  if (isLmaServicesEnabled()) {
    return getEnvVariable(ConfigKey.ANOMALIES_API_URL) || getEnvVariable(ConfigKey.ORGTREE_API_URL);
  }
  return getEnvVariable(ConfigKey.HTTP_API_URL);
}

export function getKeycloakConfiguration() {
  return {
    url: getEnvVariable(ConfigKey.KEYCLOAK_AUTH_SERVER_URL),
    realm: getEnvVariable(ConfigKey.KEYCLOAK_REALM),
    resource: getEnvVariable(ConfigKey.KEYCLOAK_CLIENT),
  };
}

export function getMapboxStyles() {
  return {
    default: getEnvVariable(ConfigKey.MAPBOX_STYLE_DEFAULT),
    satellite: getEnvVariable(ConfigKey.MAPBOX_STYLE_SATELLITE),
    dark: getEnvVariable(ConfigKey.MAPBOX_STYLE_DARK),
    stopp: getEnvVariable(ConfigKey.MAPBOX_STYLE_STOPP),
    stoppSat: getEnvVariable(ConfigKey.MAPBOX_STYLE_STOPPSAT),
  };
}
